import React, { useState, useMemo, useEffect } from 'react';
import { Layout, Menu, Button, Avatar, Dropdown, Space, Typography, theme, Modal, Form, Input, message } from 'antd';
import type { MenuProps } from 'antd';
import {
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  DashboardOutlined,
  UserOutlined,
  BookOutlined,
  AppstoreOutlined,
  SettingOutlined,
  LogoutOutlined,
  TrophyOutlined,
} from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { adminAccountApi } from '@/services/api';

const { Header, Sider, Content } = Layout;
const { Title, Text } = Typography;

const iconMap = {
  DashboardOutlined: <DashboardOutlined />,
  UserOutlined: <UserOutlined />,
  BookOutlined: <BookOutlined />,
  AppstoreOutlined: <AppstoreOutlined />,
  SettingOutlined: <SettingOutlined />,
  TrophyOutlined: <TrophyOutlined />,
};

type IconKey = keyof typeof iconMap;

interface AdminMenuLink {
  key: string;
  label: string;
  path: string;
}

interface AdminMenuGroup {
  key: string;
  label: string;
  icon: IconKey;
  links: AdminMenuLink[];
}

const dashboardMenu: AdminMenuLink & { icon: IconKey } = {
  key: 'dashboard',
  label: '대시보드',
  path: '/admin/dashboard',
  icon: 'DashboardOutlined',
};

const adminMenuGroups: AdminMenuGroup[] = [
  {
    key: 'student-group',
    label: '학생관리',
    icon: 'UserOutlined',
    links: [
      { key: 'student-management', label: '학생관리', path: '/admin/student' },
      { key: 'trophy-badge-award', label: '트로피 뱃지 부여', path: '/admin/trophy-badge' },
      { key: 'trophy-badge-master-management', label: '트로피 뱃지 관리', path: '/admin/trophy-badge-master' },
    ],
  },
  {
    key: 'academy-group',
    label: '학원관리',
    icon: 'AppstoreOutlined',
    links: [
      { key: 'academy-list', label: '학원관리', path: '/admin/academy/list' },
      { key: 'teacher-management', label: '강사관리', path: '/admin/teacher' },
      { key: 'registration-management', label: '가입신청 관리', path: '/admin/registration' },
    ],
  },
  {
    key: 'content-group',
    label: '콘텐츠관리',
    icon: 'BookOutlined',
    links: [
      { key: 'word-management', label: '단어관리', path: '/admin/word' },
      { key: 'wordbook-management', label: '단어장관리', path: '/admin/wordbook' },
      { key: 'sentence-management', label: '문장관리', path: '/admin/sentence' },
      { key: 'unit-management', label: '유닛관리', path: '/admin/unit' },
      { key: 'textbook-management', label: '교재관리', path: '/admin/textbook' },
      { key: 'word-exam-management', label: '단어시험 관리', path: '/admin/word-exam' },
    ],
  },
  {
    key: 'learning-group',
    label: '학습관리',
    icon: 'SettingOutlined',
    links: [
      { key: 'learning-management', label: '학습 및 숙제 배분', path: '/admin/learning' },
      { key: 'learning-grading', label: '숙제 채점하기', path: '/admin/learning?view=grading' },
    ],
  },
  {
    key: 'online-exam-group',
    label: '온라인 시험관리',
    icon: 'TrophyOutlined',
    links: [
      { key: 'paper-management', label: '온라인시험지 등록', path: '/admin/paper' },
      { key: 'exam-result-management', label: '시험 재첨하기', path: '/admin/exam-result' },
    ],
  },
];

interface AdminLayoutProps {
  children: React.ReactNode;
}

const AdminLayout: React.FC<AdminLayoutProps> = ({ children }) => {
  const [collapsed, setCollapsed] = useState(false);
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [isPasswordUpdating, setIsPasswordUpdating] = useState(false);
  const [passwordForm] = Form.useForm();
  const navigate = useNavigate();
  const location = useLocation();
  // 간단한 인증 가드: 토큰 없으면 로그인으로
  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    const isLoginPage = location.pathname.startsWith('/admin/login');
    if (!token && !isLoginPage) {
      navigate('/admin/login', { replace: true });
    }
  }, [location.pathname, navigate]);

  const openPasswordModal = () => {
    passwordForm.resetFields();
    setIsPasswordModalOpen(true);
  };

  const closePasswordModal = () => {
    if (isPasswordUpdating) return;
    setIsPasswordModalOpen(false);
    passwordForm.resetFields();
  };

  const handlePasswordChange = async () => {
    try {
      const values = await passwordForm.validateFields();
      setIsPasswordUpdating(true);

      const res = await adminAccountApi.changePassword(values);
      const success = res?.success ?? res?.data?.success;
      const responseMessage = res?.message || res?.data?.message;

      if (success) {
        message.success(responseMessage || '비밀번호가 변경되었습니다.');
        setIsPasswordModalOpen(false);
        passwordForm.resetFields();
      } else {
        message.error(responseMessage || '비밀번호 변경에 실패했습니다.');
      }
    } catch (error: any) {
      if (error?.errorFields) {
        return;
      }
      const errorMessage = error?.response?.data?.message || '비밀번호 변경 중 오류가 발생했습니다.';
      message.error(errorMessage);
    } finally {
      setIsPasswordUpdating(false);
    }
  };

  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();

  const menuKeyToPath = useMemo(() => {
    const map = new Map<string, string>();
    map.set(dashboardMenu.key, dashboardMenu.path);
    adminMenuGroups.forEach(group => {
      group.links.forEach(link => {
        map.set(link.key, link.path);
      });
    });
    return map;
  }, []);

  const menuItems = useMemo<MenuProps['items']>(() => {
    const items: MenuProps['items'] = [
      {
        key: dashboardMenu.key,
        icon: iconMap[dashboardMenu.icon],
        label: dashboardMenu.label,
      },
      { type: 'divider' as const },
    ];

    adminMenuGroups.forEach((group, index) => {
      items.push({
        key: group.key,
        type: 'group',
        label: (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 600 }}>
            {iconMap[group.icon]}
            <span>{group.label}</span>
          </div>
        ),
        children: group.links.map(link => ({
          key: link.key,
          label: link.label,
        })),
      });

      // 그룹 사이 구분선
      items.push({ type: 'divider' as const, key: `${group.key}-divider-${index}` });
    });

    return items;
  }, []);

  const selectedKey = useMemo(() => {
    const currentPath = location.pathname;
    const currentSearch = location.search || '';
    const currentFullPath = `${currentPath}${currentSearch}`;
    const searchParams = new URLSearchParams(currentSearch);
    let fallbackKey: string | undefined;

    for (const [key, fullPath] of menuKeyToPath.entries()) {
      if (fullPath === currentFullPath) {
        return key;
      }

      const [basePath, queryString] = fullPath.split('?');
      const matchesBase = currentPath === basePath || currentPath.startsWith(`${basePath}/`);

      if (!matchesBase) {
        continue;
      }

      if (queryString) {
        const expectedParams = new URLSearchParams(queryString);
        let matchesQuery = true;

        expectedParams.forEach((value, paramKey) => {
          if (searchParams.get(paramKey) !== value) {
            matchesQuery = false;
          }
        });

        if (matchesQuery) {
          return key;
        }

        continue;
      }

      if (!fallbackKey) {
        fallbackKey = key;
      }
    }

    return fallbackKey;
  }, [location.pathname, location.search, menuKeyToPath]);

  const handleMenuClick: MenuProps['onClick'] = ({ key }) => {
    const path = menuKeyToPath.get(key);
    if (path) {
      navigate(path);
    }
  };

  const userMenuItems = [
    // {
    //   key: 'profile',
    //   icon: <UserOutlined />,
    //   label: '프로필',
    // },
    // {
    //   key: 'settings',
    //   icon: <SettingOutlined />,
    //   label: '설정',
    // },
    {
      key: 'change-password',
      icon: <SettingOutlined />,
      label: '비밀번호 변경',
      onClick: openPasswordModal,
    },
    {
      type: 'divider' as const,
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: '로그아웃',
      onClick: () => {
        localStorage.removeItem('admin_token');
        navigate('/admin/login', { replace: true });
      },
    },
  ];

  return (
    <Layout style={{ minHeight: '100vh' }}>
      {/* 관리자 전용 사이드바 */}
      <Sider 
        trigger={null} 
        collapsible 
        collapsed={collapsed}
        style={{
          background: colorBgContainer,
          boxShadow: '2px 0 8px rgba(0,0,0,0.1)',
          zIndex: 1000,
        }}
      >
        <div style={{ 
          height: 64, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          borderBottom: '1px solid #f0f0f0'
        }}>
          <Title level={4} style={{ margin: 0, color: '#1890ff' }}>
            {collapsed ? 'AE' : 'Alpha Eng'}
          </Title>
        </div>
        <Menu
          mode="inline"
          selectedKeys={selectedKey ? [selectedKey] : []}
          items={menuItems}
          onClick={handleMenuClick}
          style={{ borderRight: 0 }}
        />
      </Sider>
      
      <Layout>
        {/* 관리자 전용 헤더 */}
        <Header style={{ 
          padding: '0 24px', 
          background: colorBgContainer,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          position: 'sticky',
          top: 0,
          zIndex: 999,
        }}>
          <Button
            type="text"
            icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            onClick={() => setCollapsed(!collapsed)}
            style={{
              fontSize: '16px',
              width: 64,
              height: 64,
            }}
          />
          
          <Dropdown
            menu={{ items: userMenuItems }}
            placement="bottomRight"
            arrow
          >
            <Space style={{ cursor: 'pointer' }}>
              <Avatar icon={<UserOutlined />} />
              <span>관리자</span>
            </Space>
          </Dropdown>
        </Header>

        <Modal
          title="비밀번호 변경"
          open={isPasswordModalOpen}
          onOk={handlePasswordChange}
          onCancel={closePasswordModal}
          okText="변경"
          cancelText="취소"
          confirmLoading={isPasswordUpdating}
          destroyOnClose
        >
          <Form form={passwordForm} layout="vertical" preserve={false}>
            <Form.Item
              name="currentPassword"
              label="현재 비밀번호"
              rules={[{ required: true, message: '현재 비밀번호를 입력하세요.' }]}
            >
              <Input.Password placeholder="현재 비밀번호" autoComplete="current-password" />
            </Form.Item>
            <Form.Item
              name="newPassword"
              label="새 비밀번호"
              rules={[
                { required: true, message: '새 비밀번호를 입력하세요.' },
                {
                  pattern: /^(?=.*[A-Za-z])(?=.*\d)(?=.*[^A-Za-z0-9\s]).{8,}$/,
                  message: '비밀번호는 영문, 숫자, 특수문자를 포함하여 8자 이상이어야 합니다.',
                },
              ]}
            >
              <Input.Password placeholder="새 비밀번호" autoComplete="new-password" />
            </Form.Item>
            <Form.Item
              name="confirmPassword"
              label="새 비밀번호 확인"
              dependencies={['newPassword']}
              rules={[
                { required: true, message: '새 비밀번호를 다시 입력하세요.' },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue('newPassword') === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error('새 비밀번호가 일치하지 않습니다.'));
                  },
                }),
              ]}
            >
              <Input.Password placeholder="새 비밀번호 확인" autoComplete="new-password" />
            </Form.Item>
          </Form>
          <Text type="secondary">비밀번호는 영문, 숫자, 특수문자를 포함하여 8자 이상이어야 합니다.</Text>
        </Modal>
        
        {/* 관리자 전용 콘텐츠 영역 */}
        <Content style={{
          margin: '24px',
          padding: 24,
          background: colorBgContainer,
          borderRadius: borderRadiusLG,
          minHeight: 280,
          overflow: 'auto',
        }}>
          {children}
        </Content>
      </Layout>
    </Layout>
  );
};

export default AdminLayout; 